import axios, { type AxiosProgressEvent } from 'axios';
import { emitToast } from '../contexts/ToastContext';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  FileUpload,
  ProcessingJob,
  StartProcessingRequest,
  ApiKeysUpdate,
  ApiKeyStatus,
  Organization,
  OrganizationCreate,
  PromptTemplate,
  PromptTemplateCreate,
  PromptTemplateUpdate,
  DataUtilityConfig,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://ai-qc-django-backend.fly.dev';

// Export API_BASE_URL for direct fetch calls
export { API_BASE_URL };

export interface PipelineModelsConfig {
  dimensions: string;
  dimensions_verification: string;
  material: string;
  part_category: string;
  generic: string;
  analytics: string;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Don't add Authorization header for login/register endpoints
    const isAuthEndpoint = config.url?.includes('/auth/login') || 
                           config.url?.includes('/auth/register') ||
                           config.url?.includes('/auth/token/refresh');
    
    if (!isAuthEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * In-flight refresh, shared by every request that got a 401 at the same time.
 *
 * Decision: ADR-050 — one refresh per burst, and the rotated refresh token is
 * kept. See docs/decisions/ADR-050-rotated-refresh-tokens-are-persisted.md
 */
let refreshInFlight: Promise<string> | null = null;

/**
 * Exchange the stored refresh token for a new access token, returning it.
 *
 * De-duplicated: every page issues parallel requests, so one expired access
 * token produces a burst of 401s. Without sharing, each 401 would POST its own
 * refresh — and because SIMPLE_JWT sets ROTATE_REFRESH_TOKENS, each of those
 * mints another refresh token, so the responses race and the last writer wins.
 * That is benign only while the token_blacklist app is absent; the moment it is
 * installed, every rotation but the last invalidates a token some caller has
 * already stored, and the user is logged out.
 *
 * @returns The new access token.
 * @throws If no refresh token is stored, or the server rejects it.
 */
const refreshAccessToken = (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/token/refresh/`, {
        refresh: refreshToken,
      });

      const { access, refresh } = response.data;
      localStorage.setItem('access_token', access);
      // ROTATE_REFRESH_TOKENS returns a NEW refresh token carrying a fresh
      // REFRESH_TOKEN_LIFETIME. Dropping it pinned the stored token's expiry to
      // login time, so a session could never be extended and every user was
      // logged out 7 days after signing in no matter how much they used the
      // app. Guarded because the response omits `refresh` entirely when
      // rotation is off, and storing undefined would end the session instead.
      if (refresh) {
        localStorage.setItem('refresh_token', refresh);
      }

      return access as string;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          // Shared across every 401 in this burst — see refreshAccessToken.
          const access = await refreshAccessToken();

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Show user-visible toasts for common server/network errors (skip 401 — handled above)
    const status = error.response?.status;
    if (status === 429) {
      emitToast({
        title: 'Too Many Requests',
        description: 'You are sending requests too quickly. Please wait a moment and try again.',
        variant: 'warning',
        duration: 6000,
      });
    } else if (status === 500) {
      emitToast({
        title: 'Server Error',
        description: 'Something went wrong on the server. Please try again.',
        variant: 'error',
        duration: 7000,
      });
    } else if (status === 502 || status === 503) {
      emitToast({
        title: 'Service Unavailable',
        description: 'The server is temporarily unreachable. Please try again shortly.',
        variant: 'error',
        duration: 7000,
      });
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: LoginRequest) =>
    api.post<AuthResponse>('/api/v1/auth/login/', data),
  
  register: (data: RegisterRequest) =>
    api.post<User>('/api/v1/auth/register/', data),
  
  getProfile: () =>
    api.get<User>('/api/v1/auth/me/'),
  
  getApiKeyStatus: () =>
    api.get<ApiKeyStatus>('/api/v1/auth/api-keys/'),
  
  updateApiKeys: (data: ApiKeysUpdate) =>
    api.put('/api/v1/auth/api-keys/', data),
  
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/api/v1/auth/change-password/', data),
  
  // DEPRECATED: The digipart connection settings endpoint is superseded by
  // session-based auth via digipartLogin / digipartSessionStatus below.
  // These methods are retained for backward compatibility but are not used by
  // any active component.
  getDigiPARTConnection: () =>
    api.get('/api/v1/auth/digipart-connection/'),
  
  updateDigiPARTConnection: (data: { endpoint?: string; api_key?: string }) =>
    api.put('/api/v1/auth/digipart-connection/', data),
  
  // Session-based DigiPART authentication
  digipartLogin: (data: { password: string }) =>
    api.post('/api/v1/dimensions/digipart/login/', data),
  
  digipartSessionStatus: () =>
    api.get('/api/v1/dimensions/digipart/status/'),
  
  digipartLogout: () =>
    api.post('/api/v1/dimensions/digipart/logout/'),
};

// Files API
export const filesAPI = {
  upload: (file: File, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUpload>('/api/v1/files/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },
  
  list: () =>
    api.get<FileUpload[]>('/api/v1/files/'),
  
  get: (id: string) =>
    api.get<FileUpload>(`/api/v1/files/${id}/`),
  
  delete: (id: string) =>
    api.delete(`/api/v1/files/${id}/`),
  
  preview: (id: string) =>
    api.get(`/api/v1/files/${id}/preview/`),
};

// Jobs API
export const jobsAPI = {
  analyzeColumns: (data: { file_id: string }) =>
    api.post('/api/v1/jobs/analyze_columns/', data),

  estimate: (data: StartProcessingRequest & { output_multiplier?: number }) =>
    api.post('/api/v1/jobs/estimate/', data),

  // T-67a — Part Category pre-flight: returns total_parts, uncategorised_parts, estimated_cost
  partCategoryPreflight: (catalogueId: string, maxParts?: number | null) => {
    const params = new URLSearchParams({ catalogue_id: catalogueId });
    if (maxParts) params.set('max_parts', String(maxParts));
    return api.get<{
      total_parts: number;
      uncategorised_parts: number;
      max_parts_applied: number | null;
      estimated_cost: {
        chunk_size_planned: number;
        total_chunks: number;
        input_tokens_per_chunk: number;
        output_tokens_per_chunk: number;
        total_input_tokens: number;
        total_output_tokens: number;
        per_chunk_usd: number;
        per_chunk_sgd: number;
        usd: number;
        sgd: number;
        model: string;
        output_multiplier: number;
        pricing_note: string;
      };
    }>(`/api/v1/jobs/part-category-preflight/?${params.toString()}`);
  },
  
  start: (
    data: StartProcessingRequest & { join_column?: string },
    idempotencyKey?: string,
  ) =>
    api.post<ProcessingJob & { is_replay?: boolean }>('/api/v1/jobs/start/', data, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }),
  
  list: () =>
    api.get<ProcessingJob[]>('/api/v1/jobs/'),
  
  get: (id: number | string) =>
    api.get<ProcessingJob>(`/api/v1/jobs/${id}/`),
  
  cancel: (id: number | string) =>
    api.post(`/api/v1/jobs/${id}/cancel/`),

  pause: (id: number | string) =>
    api.post(`/api/v1/jobs/${id}/pause/`),

  resume: (id: number | string) =>
    api.post<ProcessingJob>(`/api/v1/jobs/${id}/resume/`),
  
  download: (id: number | string) =>
    // Always request as blob; handler tries JSON.parse to detect Tigris pre-signed URL
    api.get(`/api/v1/jobs/${id}/download/`, { responseType: 'blob' }),

  downloadPartial: (id: number | string) =>
    // Same dual-response pattern: JSON Tigris URL or blob stream
    api.get(`/api/v1/jobs/${id}/download_partial/`, { responseType: 'blob' }),
  
  analytics: (days: number = 30, templateId?: string, category?: string, jobName?: string, organizationId?: string) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (templateId) params.append('template_id', templateId);
    if (category) params.append('category', category);
    if (jobName) params.append('job_name', jobName);
    if (organizationId) params.append('organization_id', organizationId);
    return api.get(`/api/v1/jobs/analytics/?${params.toString()}`);
  },
  
  jobNames: (category?: string, templateId?: string) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (templateId) params.append('template_id', templateId);
    return api.get(`/api/v1/jobs/job_names/?${params.toString()}`);
  },

  estimateTokens: (data: { text: string; model_name?: string }) =>
    api.post<{ estimated_tokens: number; estimated_cost_usd: number; estimated_cost_sgd: number }>(
      '/api/v1/estimate-tokens/',
      data,
    ),

  validationQueue: (
    id: number | string,
    params?: { page?: number; page_size?: number; state?: string; search?: string; sort?: string },
  ) => api.get(`/api/v1/jobs/${id}/validation-queue/`, { params }),

  saveValidation: (
    id: number | string,
    decisions: Array<{ part_id: string; selected_category_id: string | null }>,
    approveAllUnreviewed?: boolean,
  ) => api.patch(`/api/v1/jobs/${id}/save-validation/`, {
    decisions,
    ...(approveAllUnreviewed ? { approve_all_unreviewed: true } : {}),
  }),

  approveValidation: (
    id: number | string,
    decisions: Array<{ part_id: string; selected_category_id: string | null }>,
  ) => api.post(`/api/v1/jobs/${id}/approve-validation/`, { decisions }),

  exportValidation: (id: number | string, draft?: boolean) =>
    api.get(`/api/v1/jobs/${id}/export-validation/`, {
      params: draft ? { draft: 'true' } : {},
      responseType: 'blob',
    }),

  refreshTaxonomy: (id: number | string) =>
    api.post<{
      resolved_count: number;
      still_unresolved_count: number;
      total_taxonomy_categories: number;
      taxonomy_diff: {
        added_count: number;
        removed_count: number;
        added_names: string[];
        removed_names: string[];
      };
    }>(`/api/v1/jobs/${id}/refresh-taxonomy/`),

  // DigiPART catalogues for the Part Category pipeline.
  // Lives in the jobs namespace so PartCategoryWorkflow does not depend on dimensionsAPI.
  getPartCategoryCatalogues: () =>
    api.get('/api/v1/jobs/catalogues/'),
};

// Prompt Templates API
export const promptsAPI = {
  list: () =>
    api.get('/api/v1/prompts/'),
  
  get: (id: string) =>
    api.get(`/api/v1/prompts/${id}/`),
  
  create: (data: PromptTemplateCreate) =>
    api.post<PromptTemplate>('/api/v1/prompts/', data),
  
  update: (id: string, data: PromptTemplateUpdate) =>
    api.put<PromptTemplate>(`/api/v1/prompts/${id}/`, data),
  
  delete: (id: string) =>
    api.delete(`/api/v1/prompts/${id}/`),
  
  setDefault: (id: string) =>
    api.post(`/api/v1/prompts/${id}/set_default/`),
  
  toggleFavorite: (id: string) =>
    api.post(`/api/v1/prompts/${id}/toggle_favorite/`),
  
  getDefault: () =>
    api.get('/api/v1/prompts/default/'),
};

// Token Usage API
export const tokenUsageAPI = {
  list: () =>
    api.get('/api/v1/token-usage/'),
  
  summary: (days: number = 30) =>
    api.get(`/api/v1/token-usage/summary/?days=${days}`),
};

// Global Prompt Templates API
export const globalPromptsAPI = {
  list: () =>
    api.get('/api/v1/global-prompts/'),
  
  get: (id: string) =>
    api.get(`/api/v1/global-prompts/${id}/`),
  
  getByPipelineType: (pipelineType: string) =>
    api.get(`/api/v1/global-prompts/?pipeline_type=${pipelineType}`),
  
  create: (data: { name: string; category: string; description: string; template_text: string; is_active?: boolean }) =>
    api.post('/api/v1/global-prompts/', data),
  
  update: (id: string, data: Partial<{ name: string; category: string; description: string; template_text: string; is_active: boolean }>) =>
    api.patch(`/api/v1/global-prompts/${id}/`, data),
  
  delete: (id: string) =>
    api.delete(`/api/v1/global-prompts/${id}/`),
  
  categories: () =>
    api.get('/api/v1/global-prompts/categories/'),
};

// Admin API
export const adminAPI = {
  // User management
  users: {
    list: (params?: { search?: string; status?: string; is_admin?: string }) =>
      api.get<User[]>('/api/v1/admin/users/', { params }),
    
    get: (id: string) =>
      api.get<User>(`/api/v1/admin/users/${id}/`),
    
    create: (data: RegisterRequest & { is_superuser?: boolean; organization?: string }) =>
      api.post<User>('/api/v1/admin/users/', data),
    
    update: (id: string, data: Partial<User>) =>
      api.patch<User>(`/api/v1/admin/users/${id}/`, data),
    
    delete: (id: string) =>
      api.delete(`/api/v1/admin/users/${id}/`),
    
    toggleActive: (id: string) =>
      api.post<User>(`/api/v1/admin/users/${id}/toggle_active/`),
    
    toggleAdmin: (id: string) =>
      api.post<User>(`/api/v1/admin/users/${id}/toggle_admin/`),
    
    resetPassword: (id: string, new_password: string) =>
      api.post(`/api/v1/admin/users/${id}/reset_password/`, { new_password }),

    assignDigipart: (id: string, digipartUsername: string, force = false) =>
      api.post<User>(`/api/v1/admin/users/${id}/digipart_assign/`, {
        digipart_username: digipartUsername,
        force,
      }),

    revokeDigipart: (id: string) =>
      api.post<User>(`/api/v1/admin/users/${id}/digipart_revoke/`),

    stats: () =>
      api.get('/api/v1/admin/users/stats/'),
  },
  
  // Organization management
  organizations: {
    list: (params?: { search?: string; status?: string }) =>
      api.get<Organization[]>('/api/v1/admin/organizations/', { params }),
    
    get: (id: string) =>
      api.get<Organization>(`/api/v1/admin/organizations/${id}/`),
    
    create: (data: OrganizationCreate) =>
      api.post<Organization>('/api/v1/admin/organizations/', data),
    
    update: (id: string, data: Partial<Organization>) =>
      api.patch<Organization>(`/api/v1/admin/organizations/${id}/`, data),
    
    delete: (id: string) =>
      api.delete(`/api/v1/admin/organizations/${id}/`),
    
    toggleActive: (id: string) =>
      api.post<Organization>(`/api/v1/admin/organizations/${id}/toggle_active/`),
    
    stats: () =>
      api.get('/api/v1/admin/organizations/stats/'),
  },

  // System configuration
  systemConfig: {
    digipart: {
      get: () =>
        api.get<import('../types').DigipartConfigResponse>('/api/v1/admin/system-config/digipart/'),
      update: (data: import('../types').DigipartConfigUpdateRequest) =>
        api.put<import('../types').DigipartConfigResponse>('/api/v1/admin/system-config/digipart/', data),
    },
    aiModels: {
      list: () =>
        api.get<{ models: string[] }>('/api/v1/admin/system-config/ai-models/'),
    },
    pipelineModels: {
      get: () =>
        api.get<PipelineModelsConfig>('/api/v1/admin/system-config/pipeline-models/'),
      update: (data: PipelineModelsConfig) =>
        api.put<PipelineModelsConfig>('/api/v1/admin/system-config/pipeline-models/', data),
    },
  },
};

// Data Utilities API
export const dataUtilitiesAPI = {
  // List available utilities
  utilities: () =>
    api.get('/api/v1/data-utilities/utilities/'),
  
  // Upload file and create job
  upload: (file: File, utilityType: string, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('utility_type', utilityType);
    return api.post('/api/v1/data-utilities/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },
  
  // List user's utility jobs
  list: () =>
    api.get('/api/v1/data-utilities/'),
  
  // Get job details
  get: (id: string) =>
    api.get(`/api/v1/data-utilities/${id}/`),
  
  // Execute utility with configuration
  execute: (id: string, config: DataUtilityConfig) =>
    api.post(`/api/v1/data-utilities/${id}/execute/`, { config }),
  
  // Download results
  download: (id: string) =>
    api.get(`/api/v1/data-utilities/${id}/download/`, {
      responseType: 'blob',
    }),
  
  // Get preview of uploaded file
  preview: (id: string) =>
    api.get(`/api/v1/data-utilities/${id}/preview/`),
  
  // Validate numeric columns before processing
  validate: (id: string, columns: string[], columnNames?: Record<string, string>) =>
    api.post(`/api/v1/data-utilities/${id}/validate/`, {
      columns,
      column_names: columnNames
    }),
};

// Organizations API
export const organizationsAPI = {
  list: () =>
    api.get<Organization[]>('/api/v1/organizations/'),
  
  get: (id: string) =>
    api.get<Organization>(`/api/v1/organizations/${id}/`),
  
  create: (data: OrganizationCreate) =>
    api.post<Organization>('/api/v1/organizations/', data),
  
  update: (id: string, data: Partial<Organization>) =>
    api.patch<Organization>(`/api/v1/organizations/${id}/`, data),
  
  delete: (id: string) =>
    api.delete(`/api/v1/organizations/${id}/`),

  // Returns only active organisations — use for dropdown pickers
  active: () =>
    api.get<Organization[]>('/api/v1/organizations/active/'),
};

// DigiPART Dimensions API
export const dimensionsAPI = {
  testConnection: () =>
    api.post('/api/v1/dimensions/test-connection/'),

  getCatalogues: () =>
    api.get('/api/v1/dimensions/catalogues/'),

  
  queryParts: (filters?: { catalogue_id?: string; part_number?: string; shape?: string; in_scope?: boolean; first?: number; has_dimensions?: boolean }) =>
    api.post('/api/v1/dimensions/query-parts/', filters || {}),
  
  getPartDetail: (partId: string) =>
    api.get(`/api/v1/dimensions/parts/${partId}/`),
  
  getPartDimensions: (partId: string) =>
    api.get(`/api/v1/dimensions/parts/${partId}/dimensions/`),
  
  getPartDocuments: (partId: string) =>
    api.get(`/api/v1/dimensions/parts/${partId}/documents/`),

  // Download decoded_dimension_export.csv + parts_no_dimensions_export.csv as a ZIP
  downloadCsv: (jobId: string) =>
    api.get(`/api/v1/dimensions/jobs/${jobId}/download-csv/`, {
      responseType: 'blob',
    }),

  // Download the dimension audit Excel workbook (original vs final comparison)
  downloadExcel: (jobId: string) =>
    api.get(`/api/v1/dimensions/jobs/${jobId}/download-excel/`, {
      responseType: 'blob',
    }),

  // Estimate AI cost before running a job (uses already-loaded part data — no DigiPART re-fetch)
  estimateCost: (payload: {
    parts: { id: string; part_number: string; shape: string }[];
    model_name?: string;
    output_token_multiplier?: number;
  }) =>
    api.post('/api/v1/dimensions/estimate-cost/', payload),

  // Fetch available models + pricing (never hardcode pricing in the frontend)
  getPricing: () =>
    api.get('/api/v1/dimensions/pricing/'),
};

// AI models available to the signed-in user's key, sorted alphabetically (PDR-002)
export const aiAPI = {
  getAvailableModels: () =>
    api.get<{ models: string[] }>('/api/v1/ai/models/'),
};

export default api;
